import { useTranslation } from 'react-i18next'
import { FormProvider } from 'react-hook-form'
import { IdCard, Stepper } from '@/components/molecules'
import type { StepperItem } from '@/components/molecules'
import { H2, Text, Button, LoadingDots } from '@/components/atoms'
import { useOnboardingPetForm } from './page.onboarding.pet-form.hook'
import { PetFormStepOnePage } from './step-one/page.onboarding.pet-form-step-one'
import { PetFormStepTwoPage } from './step-two/page.onboarding.pet-form-step-two'
import { formatDate } from '@/shared/helpers/format-date'
import { useUser } from '@/shared/hooks/use-user'

export function OnboardingPetFormPage() {
  const { t } = useTranslation()
  const {
    currentStep,
    isFlipping,
    formMethods,
    isDataLoading,
    isSubmitting,
    cardImageUrl,
    hidePhotoBackground,
    cardBirthDate,
    cardWeightKey,
    cardVetName,
    handleNext,
    handleBack,
    handleSkip,
    onboardingPet,
    onboardingPolicy,
    hideVetPrimaryField,
  } = useOnboardingPetForm()
  const { user } = useUser()

  const cardWeightLabel = cardWeightKey
    ? t(`onboarding.pet.form.stepOne.weightOptions.${cardWeightKey}`)
    : ''

  const steps: StepperItem[] = [
    {
      label: t('onboarding.pet.form.stepOne.stepLabel'),
      children: <PetFormStepOnePage pet={onboardingPet} />,
    },
    {
      label: t('onboarding.pet.form.stepTwo.stepLabel'),
      children: (
        <PetFormStepTwoPage
          pet={onboardingPet}
          showVetPrimary={hideVetPrimaryField}
        />
      ),
    },
  ]

  const isLastStep = currentStep === steps.length - 1
  const startDate = formatDate(onboardingPolicy?.startDate)

  if (isDataLoading) {
    return (
      <div className="flex min-h-full items-center justify-center bg-white">
        <LoadingDots className="loading-xl" />
      </div>
    )
  }

  return (
    <FormProvider {...formMethods}>
      <div className="flex min-h-full flex-col bg-white">
        <main className="flex grow justify-center px-6 py-8 md:px-18">
          <div className="grid w-full max-w-5xl grid-cols-1 gap-8 overflow-hidden md:grid-cols-2">
            <div className="z-10 hidden shrink-0 flex-col gap-8 bg-white md:flex">
              <div
                className="transition-transform duration-300"
                style={{
                  perspective: '1000px',
                  transformStyle: 'preserve-3d',
                  transform: isFlipping ? 'rotateY(90deg)' : 'rotateY(0deg)',
                }}
              >
                {isLastStep ? (
                  <IdCard
                    variant="no-photo"
                    policyId={`#${onboardingPolicy?.policyExternalId ?? ''}`}
                    birthday={cardBirthDate}
                    weight={cardWeightLabel}
                    vetName={cardVetName}
                    isMobile={false}
                    className="max-w-84"
                  />
                ) : (
                  <IdCard
                    variant="with-photo"
                    planName={onboardingPet?.name ?? ''}
                    ownerName={user?.fullName ?? ''}
                    policyId={`#${onboardingPolicy?.policyExternalId ?? ''}`}
                    startDate={startDate}
                    photoUrl={cardImageUrl}
                    hidePhotoBackground={hidePhotoBackground}
                    isMobile={false}
                    className="max-w-84"
                  />
                )}
              </div>
              <div className="flex flex-col gap-6">
                <H2>
                  {`${t('onboarding.pet.form.titleStart')} `}
                  <span className="text-indigo-base capitalize">{`${onboardingPet?.name}!`}</span>
                </H2>
                <Text className="text-neutral-600">
                  {t('onboarding.pet.form.subtitle')}
                </Text>
              </div>
            </div>
            <div className="flex flex-1 flex-col">
              <Stepper items={steps} currentStep={currentStep} dynamicHeight />
            </div>
          </div>
        </main>
        <footer className="sticky bottom-0 z-10 flex shrink-0 items-stretch justify-stretch gap-4 bg-linear-to-b from-transparent from-0% via-white via-19% to-white to-100% px-6 py-4 pt-6 md:items-center md:justify-end md:bg-indigo-50 md:bg-none md:px-21.5">
          {isLastStep && (
            <Button
              variant="tertiary"
              size="lg"
              className="hidden! md:flex!"
              onClick={handleBack}
              data-testid="pet-form-back"
            >
              {t('onboarding.pet.form.back')}
            </Button>
          )}
          <Button
            variant="secondary"
            size="lg"
            sizeMobile="md"
            className="flex-1 md:flex-none"
            onClick={handleSkip}
            data-testid="pet-form-skip"
          >
            {t('onboarding.pet.form.remindLater')}
          </Button>
          {isLastStep ? (
            <Button
              size="lg"
              sizeMobile="md"
              className="flex-1 md:flex-none"
              loading={isSubmitting}
              onClick={handleNext}
              data-testid="pet-form-finish"
            >
              {t('onboarding.pet.form.finished')}
            </Button>
          ) : (
            <Button
              size="lg"
              sizeMobile="md"
              className="flex-1 md:flex-none"
              onClick={handleNext}
              data-testid="pet-form-continue"
            >
              {t('onboarding.pet.form.continue')}
            </Button>
          )}
        </footer>
      </div>
    </FormProvider>
  )
}
